import { useMutation, useQueryClient } from '@tanstack/react-query';
import { productKeys } from '@/services/queryKeys';
import { productService } from '@/services/product.service';
import type {
  CreateProductInput,
  UpdateProductInput,
  ProductState,
  VariantUpsertInput,
} from '@tcr-erp/shared';

/**
 * Variant whitelist khớp với VariantUpsertInput. BE từ chối field ngoài whitelist
 * (`forbidNonWhitelisted: true`). Loại các field server-managed như
 * is_price_paused/price_paused_*, cached_*, tenant_id, product_id, timestamps.
 */
type VariantLogisticsKey = 'weight_g' | 'length_cm' | 'width_cm' | 'height_cm';
type VariantSubmitKey = keyof VariantUpsertInput | 'unit_price' | VariantLogisticsKey | 'min_stock_quantity';

const VARIANT_SUBMIT_KEYS: VariantSubmitKey[] = [
  '_id',
  'variant_code',
  'is_default',
  'attrs',
  'unit_price',
  'weight_g',
  'length_cm',
  'width_cm',
  'height_cm',
  'min_stock_quantity',
  'is_sellable',
  'is_sellable_web',
  'is_sellable_pos',
  'is_sellable_app',
  'is_published',
  'image_intro',
];

function sanitizeVariant(v: Record<string, unknown>): VariantUpsertInput {
  const out: Record<string, unknown> = {};
  for (const key of VARIANT_SUBMIT_KEYS) {
    if (v[key] !== undefined) out[key] = v[key];
  }
  return out as unknown as VariantUpsertInput;
}

function sanitizeBody<T extends { variants?: unknown }>(body: T): T {
  if (!Array.isArray(body.variants)) return body;
  return {
    ...body,
    variants: (body.variants as Record<string, unknown>[]).map(sanitizeVariant),
  };
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductInput) => productService.create(sanitizeBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateProductInput }) =>
      productService.update(id, sanitizeBody(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProductState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: ProductState }) =>
      productService.updateState(id, state),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useCloneProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productService.clone(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useBulkUpdateProductState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, state }: { ids: string[]; state: ProductState }) =>
      productService.bulkUpdateState(ids, state),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useBulkUpdateProductCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids: string[]; category_ids: string[]; mode: 'replace' | 'append' }) =>
      productService.bulkUpdateCategory(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useCreateProductVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, body }: { productId: string; body: VariantUpsertInput }) =>
      productService.createVariant(productId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}

export function useUpdateProductVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, variantId, body }: { productId: string; variantId: string; body: VariantUpsertInput }) =>
      productService.updateVariant(productId, variantId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
  });
}
